
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { isSuperAdmin } from '@/services/admin.service';

// Cache for admin status to reduce database calls
const adminStatusCache = new Map<string, { status: boolean; timestamp: number; ttl: number }>();
const CACHE_TTL = 10 * 60 * 1000; // 10 minutes
const MAX_RETRIES = 3; // Reduced from 5
const BASE_DELAY = 2000; // 2 seconds

interface AdminStatusState {
  isSuperAdmin: boolean;
  isLoading: boolean;
  error: string | null;
  retryCount: number;
  canRetry: boolean;
  lastAttempt: number | null;
}

export const useAdminStatus = () => {
  const { user } = useAuth();
  const [state, setState] = useState<AdminStatusState>({
    isSuperAdmin: false,
    isLoading: true,
    error: null,
    retryCount: 0,
    canRetry: true,
    lastAttempt: null
  });
  
  const timeoutRef = useRef<NodeJS.Timeout>();
  const circuitBreakerRef = useRef<{ failures: number; lastFailure: number; isOpen: boolean }>({
    failures: 0,
    lastFailure: 0,
    isOpen: false
  });

  const getCachedStatus = (userId: string): boolean | null => {
    const cached = adminStatusCache.get(userId);
    if (cached && Date.now() - cached.timestamp < cached.ttl) {
      console.log('Using cached admin status:', cached.status);
      return cached.status;
    }
    return null;
  };

  const setCachedStatus = (userId: string, status: boolean, ttl = CACHE_TTL) => {
    adminStatusCache.set(userId, {
      status,
      timestamp: Date.now(),
      ttl
    });
  };

  const checkCircuitBreaker = (): boolean => {
    const cb = circuitBreakerRef.current;
    const now = Date.now();
    
    // Reset circuit breaker after 1 minute
    if (cb.isOpen && now - cb.lastFailure > 60000) {
      console.log('Circuit breaker reset after timeout');
      cb.isOpen = false;
      cb.failures = 0;
    }
    
    // Open circuit breaker after 2 consecutive failures (reduced from 3)
    if (cb.failures >= 2 && !cb.isOpen) {
      console.log('Circuit breaker opened due to failures');
      cb.isOpen = true;
    }
    
    return !cb.isOpen;
  };

  const checkAdminStatus = async (attempt = 1): Promise<void> => {
    if (!user?.id) {
      setState(prev => ({
        ...prev,
        isSuperAdmin: false,
        isLoading: false,
        error: null
      }));
      return;
    }

    // Check cache first - always prefer cached results
    const cachedStatus = getCachedStatus(user.id);
    if (cachedStatus !== null) {
      setState(prev => ({
        ...prev,
        isSuperAdmin: cachedStatus,
        isLoading: false,
        error: null,
        retryCount: attempt - 1
      }));
      return;
    }

    // Check circuit breaker
    if (!checkCircuitBreaker()) {
      setState(prev => ({
        ...prev,
        isSuperAdmin: false,
        isLoading: false,
        error: 'Admin verification temporarily unavailable',
        canRetry: true
      }));
      return;
    }

    try {
      console.log(`Admin status check attempt ${attempt} for user:`, user.id);
      setState(prev => ({ ...prev, lastAttempt: Date.now() }));
      
      const isAdmin = await isSuperAdmin(user.id);
      console.log('Admin status check result:', isAdmin);
      
      // Success - cache the result and reset error state
      setCachedStatus(user.id, isAdmin);
      circuitBreakerRef.current.failures = 0;
      
      setState(prev => ({
        ...prev,
        isSuperAdmin: isAdmin,
        isLoading: false,
        error: null,
        retryCount: attempt - 1,
        canRetry: true
      }));
      
    } catch (error) {
      console.error(`Error checking admin status (attempt ${attempt}):`, error);
      circuitBreakerRef.current.failures++;
      circuitBreakerRef.current.lastFailure = Date.now();
      
      const errorMessage = error instanceof Error ? error.message : 'Failed to verify admin status';
      const isNetworkError = errorMessage.includes('Load failed') || errorMessage.includes('Network');
      
      // Try to use extended cache on failure
      const previousCachedStatus = adminStatusCache.get(user.id);
      const canUseExtendedCache = previousCachedStatus && Date.now() - previousCachedStatus.timestamp < CACHE_TTL * 2;
      
      if (canUseExtendedCache) {
        console.log('Using extended cache due to verification failure');
        setState(prev => ({
          ...prev,
          isSuperAdmin: previousCachedStatus.status,
          isLoading: false,
          error: `Using cached admin status due to connectivity issues`,
          retryCount: attempt - 1,
          canRetry: true
        }));
        return;
      }
      
      // Implement exponential backoff for retries
      if (attempt < MAX_RETRIES && isNetworkError) {
        const delay = Math.min(BASE_DELAY * Math.pow(2, attempt - 1), 10000); // Cap at 10 seconds
        console.log(`Retrying admin status check in ${delay}ms... (attempt ${attempt + 1}/${MAX_RETRIES})`);
        
        setState(prev => ({ 
          ...prev, 
          retryCount: attempt,
          error: `Verifying admin access... (attempt ${attempt}/${MAX_RETRIES})`
        }));
        
        timeoutRef.current = setTimeout(() => {
          checkAdminStatus(attempt + 1);
        }, delay);
        return;
      }
      
      // Final failure
      setState(prev => ({
        ...prev,
        isSuperAdmin: false,
        isLoading: false,
        error: errorMessage,
        retryCount: attempt - 1,
        canRetry: attempt < MAX_RETRIES
      }));
    }
  };

  useEffect(() => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    checkAdminStatus(1);
    
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [user?.id]);

  const manualRetry = () => {
    if (user?.id) {
      // Don't clear cache on manual retry - just reset circuit breaker
      circuitBreakerRef.current = { failures: 0, lastFailure: 0, isOpen: false };
      
      setState(prev => ({ 
        ...prev, 
        isLoading: true, 
        error: null, 
        retryCount: 0 
      }));
      checkAdminStatus(1);
    }
  };

  return { 
    isSuperAdmin: state.isSuperAdmin,
    isLoading: state.isLoading,
    error: state.error,
    retryCount: state.retryCount,
    canRetry: state.canRetry,
    lastAttempt: state.lastAttempt,
    retry: manualRetry
  };
};
